/**
 * Color utility functions for dynamic theme generation.
 * Derives a full MUI palette from primary + secondary hex colors.
 */

type RGB = { r: number; g: number; b: number };
type HSL = { h: number; s: number; l: number };

function hexToRgb(hex: string): RGB {
	const clean = hex.replace("#", "");
	return {
		r: parseInt(clean.substring(0, 2), 16),
		g: parseInt(clean.substring(2, 4), 16),
		b: parseInt(clean.substring(4, 6), 16),
	};
}

function rgbToHex({ r, g, b }: RGB): string {
	const toHex = (n: number) =>
		Math.max(0, Math.min(255, Math.round(n)))
			.toString(16)
			.padStart(2, "0");
	return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function rgbToHsl({ r, g, b }: RGB): HSL {
	const rN = r / 255;
	const gN = g / 255;
	const bN = b / 255;
	const max = Math.max(rN, gN, bN);
	const min = Math.min(rN, gN, bN);
	const l = (max + min) / 2;
	let h = 0;
	let s = 0;

	if (max !== min) {
		const d = max - min;
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
		switch (max) {
			case rN:
				h = ((gN - bN) / d + (gN < bN ? 6 : 0)) / 6;
				break;
			case gN:
				h = ((bN - rN) / d + 2) / 6;
				break;
			case bN:
				h = ((rN - gN) / d + 4) / 6;
				break;
		}
	}

	return { h: h * 360, s: s * 100, l: l * 100 };
}

function hslToRgb({ h, s, l }: HSL): RGB {
	const sN = s / 100;
	const lN = l / 100;
	const hN = h / 360;

	if (sN === 0) {
		const v = Math.round(lN * 255);
		return { r: v, g: v, b: v };
	}

	const hue2rgb = (p: number, q: number, t: number) => {
		let tN = t;
		if (tN < 0) tN += 1;
		if (tN > 1) tN -= 1;
		if (tN < 1 / 6) return p + (q - p) * 6 * tN;
		if (tN < 1 / 2) return q;
		if (tN < 2 / 3) return p + (q - p) * (2 / 3 - tN) * 6;
		return p;
	};

	const q = lN < 0.5 ? lN * (1 + sN) : lN + sN - lN * sN;
	const p = 2 * lN - q;

	return {
		r: Math.round(hue2rgb(p, q, hN + 1 / 3) * 255),
		g: Math.round(hue2rgb(p, q, hN) * 255),
		b: Math.round(hue2rgb(p, q, hN - 1 / 3) * 255),
	};
}

export function lighten(hex: string, amount: number): string {
	const hsl = rgbToHsl(hexToRgb(hex));
	hsl.l = Math.min(100, hsl.l + amount);
	return rgbToHex(hslToRgb(hsl));
}

export function darken(hex: string, amount: number): string {
	const hsl = rgbToHsl(hexToRgb(hex));
	hsl.l = Math.max(0, hsl.l - amount);
	return rgbToHex(hslToRgb(hsl));
}

/**
 * Returns relative luminance of a color (0-1).
 * Used for WCAG contrast calculations.
 */
function getLuminance(hex: string): number {
	const { r, g, b } = hexToRgb(hex);
	const [rL, gL, bL] = [r, g, b].map((c) => {
		const s = c / 255;
		return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
	});
	return 0.2126 * rL + 0.7152 * gL + 0.0722 * bL;
}

/**
 * Returns black or white text color for best contrast (WCAG AA).
 */
export function getContrastText(hex: string): string {
	return getLuminance(hex) > 0.179 ? "#000000" : "#FFFFFF";
}

/**
 * Documented global default for the `tertiary` palette role (JANUS lilac,
 * `--brand-tertiary-main`). Used when a tenant provides no `tertiaryColor`
 * (SIMS-7505 AC2) — a distinct constant, NOT derived from primary/secondary.
 */
export const DEFAULT_TERTIARY_COLOR = "#7034E7";

/**
 * Documented global default for the `surfaceTint` palette role (JANUS
 * `--surface-tint` / `--janus-blue-100`). Used when a tenant provides no
 * `surfaceTint` (SIMS-7608 AC2) — a distinct role, NOT `surfaceLight`/`#FFFFFF`
 * or a derived value. Per-tenant values (SIMS #E9D0F4, …) are backfilled in SIMS-7513.
 */
export const DEFAULT_SURFACE_TINT = "#DFF5FF";

/**
 * Documented global defaults for the `success` pair (SIMS-8458 AC1).
 *
 * The design's `--success-light` / `--success-dark`. Until this landed, `generatePalette`
 * emitted no `success` key at all, so `createTheme` merged MUI's stock green
 * (`#4caf50` / `#1b5e20`) — which is why the Active status pill, the "ok" journey-step
 * icon and the Incentive History amount all rendered green rather than JANUS lime. Those
 * three consumers already read `palette.success.*`, so defining the role is the whole fix;
 * none of them needed a component change.
 *
 * A DISTINCT role, deliberately not `outlineDark` (SIMS-8458 notes): for the JANUS tenant
 * `outlineDark` happens to hold `#AEDD49` too, but it also drives ~38 borders AND the
 * Excused-absence / Missed-visit clinical status colours (`general.ts:30/34`). Aliasing the
 * two would silently recolour those clinical states — AC5 forbids it, and
 * `__tests__/utils/clinicalStatusColors.guard.test.ts` fails if anyone tries.
 *
 * Accessibility, flagged on the ticket and unresolved: `#408103` on `#AEDD49` is 3.04:1 —
 * below WCAG AA 4.5 for normal text, though it clears the 3:1 bar for large/bold text and
 * UI components. The Active pill's Chip label is `size="small"`, i.e. normal text.
 */
export const DEFAULT_SUCCESS_LIGHT = "#AEDD49";
export const DEFAULT_SUCCESS_DARK = "#408103";

/**
 * Documented global default for the `brandFaint` role (SIMS-8458 AC2) — the design's
 * `--brand-primary-faint`, used by the selected day card in Daily Journey.
 *
 * A DISTINCT role, deliberately NOT a re-derived `primary.light` (SIMS-8458 notes):
 * `primary.light` is consumed by `MenuItemIcon` / `MenuItemButton` for inactive sidebar
 * text, so deriving this from it would tie the selected-day treatment to the menu's
 * appearance. AC2 also requires it be "visibly distinct from the sidebar/menu colour".
 */
export const DEFAULT_BRAND_FAINT = "#5AAB96";

/**
 * Documented global default for the `surfaceTwo` role (SIMS-8458 AC11) — the design's
 * `--surface-2`.
 *
 * A DISTINCT role rather than a derived one. The Daily Journey "not started yet" step
 * icon previously used `surface.dark`, which is `darken(surfaceColor, 8)`: it drifts with
 * whatever surface a tenant configures and renders a teal-tinted grey (#c6d6d6 for JANUS)
 * where the design wants a cool grey. SIMS-7532 AC1 names four step-icon colours —
 * grey / orange / green / red — and this is the grey.
 *
 * Same value as `divider` on purpose: the design defines `--divider: var(--surface-2)`,
 * so the framed-table border and this surface are one token. They are kept as separate
 * roles because they are separate *concepts* — a tenant could reasonably retint one
 * without the other — but they share a default.
 */
export const DEFAULT_SURFACE_TWO = "#D2DCDF";

/**
 * Documented global defaults for the `warning` pair (SIMS-8458 AC10) — the design's
 * `--warning-light` / `--warning-dark`.
 *
 * The light half was `#FFE7E0`, a pale PINK, where the design specifies `#FFE7C2`, a warm
 * amber. Both the Daily Journey step icons and the day-detail status pills read this pair
 * (the authoring source uses `var(--warning-light)` for both), so "Pending" and "Overdue"
 * rendered pinkish rather than the orange SIMS-7532 AC1 calls for. The dark half was
 * already correct.
 *
 * Unlike the roles above, `warning` stays FIXED — not exposed in the colour settings
 * screen. It is a semantic role: orange means "awaiting action" regardless of tenant
 * brand. (`success` is configurable via AC7 only because AC1 had to define a role that
 * did not exist; that asymmetry is acknowledged on the ticket and left to a separate
 * theming-policy decision rather than settled here.)
 */
export const DEFAULT_WARNING_LIGHT = "#FFE7C2";
export const DEFAULT_WARNING_DARK = "#F26716";

/**
 * Documented global default for MUI's `divider` role (SIMS-8458 AC4) — the design's
 * `--divider`.
 *
 * `generatePalette` never emitted a `divider`, so MUI merged its own
 * `rgba(0, 0, 0, 0.12)`. Being translucent, that composites differently against whatever
 * sits behind it — `#E0E0E0` on white, `#D7DADB` on a `--surface-1` zebra row — so the
 * same border read as two different greys within one table. A solid value is the same
 * everywhere, which is the real win here; the colour shift itself is slight
 * (luminance 0.697 → 0.702 over `--surface-1`).
 *
 * ~42 call sites read it, roughly 28 of them outside the screens this initiative
 * restyled — app-wide is intended for a foundation ticket. AC5's "existing borders are
 * unchanged" refers to the `outline.dark`-drawn borders, which this does not touch.
 *
 * The design defines `--divider: var(--surface-2)`, and the JANUS block sets
 * `--surface-2: #D2DCDF` — so the divider and the "Completed" pill's grey surface are the
 * same token, which is why AC4 names one value for both.
 */
export const DEFAULT_DIVIDER = "#D2DCDF";

/**
 * Documented global defaults for body and secondary text (SIMS-8458 AC4) — the design's
 * `--text-primary` / `--text-secondary`, read from the authoring source's `:root` block.
 *
 * AC4 asks that these resolve "across every theme role documented as carrying that text".
 * The documenting artifact is `THEME_COLOR_MAP.md`, which resolves it to six roles:
 * body text — `textMain.dark2`, `textPrimary.main`, MUI `text.primary`; secondary/muted —
 * MUI `text.secondary`, `textMain.main2`, `textSecondary.main`. Roughly 310 call sites, and
 * afterwards the app's three parallel text families are aliases of these two values rather
 * than three competing systems.
 *
 * NOT in scope, recorded so the gap is visible rather than silently half-done: the design
 * also carries `--text-disabled` / `--text-placeholder` `#B0B6B6` (nearest role
 * `textMain.dark`, `#CCCCCE`, ~74 sites — the largest single text role in the app) and
 * `--text-link`, which the design derives from brand-primary where the app derives
 * `textLinkHelpSupport` from `secondaryColor`. Neither is body or muted text, so AC4's
 * wording does not reach them; each wants its own ticket.
 */
export const DEFAULT_TEXT_PRIMARY = "#1F2626";
export const DEFAULT_TEXT_SECONDARY = "#575C5C";

export interface ThemeColorsConfig {
	primaryColor: string;
	secondaryColor: string;
	surfaceColor?: string;
	surfaceLight?: string;
	surfaceDark?: string;
	primaryDark?: string;
	secondaryLight?: string;
	outlineMain?: string;
	outlineDark?: string;
	textMainDark2?: string;
	textMainMain2?: string;
	textSecondaryMain?: string;
	tertiaryColor?: string;
	surfaceTint?: string;
	// SIMS-8458 — design-system roles, configurable per tenant (AC7/AC8). Each falls back
	// to its documented default when the tenant has not overridden it, and SettingsModal
	// emits it into `themeColors` ONLY when it differs from that default, so defaults stay
	// implicit and a later change to a default still reaches tenants that never set one.
	successLight?: string;
	successDark?: string;
	brandFaint?: string;
	dividerColor?: string;
	surfaceTwo?: string;
	textPrimaryMain?: string;
	fontFamily?: string;
}

/**
 * Generates the full themePalette structure from theme color configuration.
 *
 * Based on the B2D brand spec:
 *  - primaryColor (#276048) -> navigation, menu, buttons, key highlights, accent
 *  - secondaryColor (#70A815) -> secondary actions, links, badges
 *  - surfaceColor (#DEE7E7) -> backgrounds, cards, surfaces
 *  - fontFamily (Roboto) -> font across the UI (weights: Regular, Medium, Semibold, Bold)
 */
export function generatePalette(config: ThemeColorsConfig) {
	const { primaryColor, secondaryColor, surfaceColor } = config;

	// Tertiary is a distinct role (JANUS lilac accent), NOT derived from
	// primary/secondary; falls back to the documented default (SIMS-7505 AC2).
	const tertiaryColor = config.tertiaryColor || DEFAULT_TERTIARY_COLOR;

	// surfaceTint is a distinct tinted-surface role (JANUS blue-100), NOT
	// surfaceLight/#FFFFFF or derived; falls back to the documented default (SIMS-7608 AC2).
	const surfaceTintColor = config.surfaceTint || DEFAULT_SURFACE_TINT;

	// SIMS-8458 — distinct design-system roles, each falling back to its documented
	// default (AC1/AC2/AC4). See the DEFAULT_* constants above for why none of these is
	// derived from an existing role.
	const successLight = config.successLight || DEFAULT_SUCCESS_LIGHT;
	const successDark = config.successDark || DEFAULT_SUCCESS_DARK;
	const brandFaintColor = config.brandFaint || DEFAULT_BRAND_FAINT;
	const dividerColor = config.dividerColor || DEFAULT_DIVIDER;
	const surfaceTwoColor = config.surfaceTwo || DEFAULT_SURFACE_TWO;
	const textPrimaryMain = config.textPrimaryMain || DEFAULT_TEXT_PRIMARY;
	const textSecondaryMain = config.textSecondaryMain || DEFAULT_TEXT_SECONDARY;

	const primaryDark = config.primaryDark || darken(primaryColor, 12);
	// primaryLight is used for inactive menu text/icons on the dark sidebar.
	// Derive a desaturated, high-lightness tint from the primary hue.
	// This matches the original purple behavior (#BAA3CA: h=275, s=27, l=72).
	const primaryHsl = rgbToHsl(hexToRgb(primaryColor));
	const primaryLight = rgbToHex(
		hslToRgb({ h: primaryHsl.h, s: Math.min(primaryHsl.s, 25), l: 72 }),
	);
	const primaryMain = primaryColor;

	const secondaryDark = darken(secondaryColor, 20);
	const secondaryLight = config.secondaryLight || lighten(secondaryColor, 30);

	// Surface: use explicit surfaceColor if provided, otherwise derive from primary
	const surfaceMain = surfaceColor || lighten(primaryColor, 42);
	const surfaceDark =
		config.surfaceDark ||
		(surfaceColor ? darken(surfaceColor, 8) : lighten(primaryColor, 35));
	const surfaceLight = config.surfaceLight || "#FFFFFF";

	// Outline colors derived from surface (overridable)
	const outlineMain =
		config.outlineMain || (surfaceColor ? darken(surfaceColor, 12) : "#D9D1DD");
	const outlineLight = surfaceColor ? lighten(surfaceColor, 5) : "#E4DEE9";
	const outlineDark =
		config.outlineDark || (surfaceColor ? darken(surfaceColor, 25) : "#ADA6B3");

	// Icon colors: neutral with primary accent
	const iconMain = surfaceColor ? darken(surfaceColor, 30) : "#ADA6B3";
	const iconLight = surfaceColor ? darken(surfaceColor, 15) : "#D9D1DD";

	return {
		mode: "light",
		primary: {
			main: primaryMain,
			light: primaryLight,
			dark: primaryDark,
		},
		secondary: {
			main: secondaryColor,
			light: secondaryLight,
			dark: secondaryDark,
		},
		tertiary: {
			main: tertiaryColor,
			light: lighten(tertiaryColor, 12),
			dark: darken(tertiaryColor, 12),
		},
		surfaceTint: {
			main: surfaceTintColor,
			light: lighten(surfaceTintColor, 4),
			dark: darken(surfaceTintColor, 6),
		},
		// SIMS-8458 AC1 — the design's success pair. A distinct role: never aliased to
		// outline.dark, which carries the clinical status colours (AC5).
		success: {
			main: successDark,
			light: successLight,
			dark: successDark,
		},
		// SIMS-8458 AC2 — brand-faint, for the selected day card. Not primary.light.
		brandFaint: {
			main: brandFaintColor,
			light: brandFaintColor,
			dark: brandFaintColor,
		},
		// SIMS-8458 AC11 — the design's --surface-2, a distinct role rather than a value
		// derived from the tenant's surfaceColor.
		surfaceTwo: {
			main: surfaceTwoColor,
			light: surfaceTwoColor,
			dark: surfaceTwoColor,
		},
		// SIMS-8458 AC4 — solid divider. Previously unset, so MUI's translucent default
		// composited differently per background.
		divider: dividerColor,
		// SIMS-8458 AC4 — MUI's text roles, where body and muted text actually resolve
		// (~173 call sites). Previously left at MUI defaults.
		text: {
			primary: textPrimaryMain,
			secondary: textSecondaryMain,
		},
		error: {
			main: "#AB0707",
			light: "#FCD6D0",
			dark: "#AB0707",
		},
		warning: {
			main: "#F8955B",
			light: DEFAULT_WARNING_LIGHT,
			dark: DEFAULT_WARNING_DARK,
		},
		surface: {
			main: surfaceMain,
			light: surfaceLight,
			dark: surfaceDark,
			dark2: surfaceColor ? darken(surfaceColor, 30) : "#ADA6B3",
		},
		accent: {
			main: primaryDark,
		},
		icon: {
			main: iconMain,
			light: iconLight,
			dark: "#4C4C56",
			dark2: primaryDark,
		},
		outline: {
			main: outlineMain,
			light: outlineLight,
			dark: outlineDark,
			dark2: "#4C4C56",
		},
		menu: {
			main: primaryDark,
			light: primaryMain,
			light2: lighten(primaryMain, 8),
		},
		textMain: {
			main: "#E5E6E6",
			// SIMS-8458 AC4 — THEME_COLOR_MAP.md: "Texto secundario/subtítulo" (~51 sites),
			// so it resolves to the design's --text-secondary. Was #79797D.
			main2: config.textMainMain2 || DEFAULT_TEXT_SECONDARY,
			main3: primaryLight,
			// Matches the design's --text-primary-inv (#FFFFFF) already — no change.
			light: "#FFFFFF",
			// NOT changed by SIMS-8458: this is the nearest role to the design's
			// --text-disabled / --text-placeholder #B0B6B6, and at ~74 sites it is the
			// largest single text role in the app. Disabled text is neither body nor muted
			// text, so AC4's wording does not reach it. Its own ticket — see the
			// DEFAULT_TEXT_PRIMARY docblock.
			dark: "#CCCCCE",
			light2: "#FCFCFC",
			// SIMS-8458 AC4 — THEME_COLOR_MAP.md: "Texto principal de toda la app"
			// (~66 sites, the app's real body-text role), so it resolves to the design's
			// --text-primary. Was #1F2026.
			dark2: config.textMainDark2 || DEFAULT_TEXT_PRIMARY,
			light3: "#F8F8F8",
			dark3: "#F26716",
			light4: "#F5F5F5",
			dark4: secondaryDark,
		},
		// SIMS-8458 AC4 — body text. THEME_COLOR_MAP.md documents this as body text, so it
		// resolves to the design's --text-primary along with textMain.dark2 and MUI
		// text.primary. Was #1F2026 (one hex digit from the design's #1F2626 — the design's
		// :root block is authoritative, and the two are not the same colour).
		textPrimary: {
			main: textPrimaryMain,
			light: textPrimaryMain,
		},
		// SIMS-8458 AC4 — THEME_COLOR_MAP.md documents this as "Labels apagados, hints de
		// formularios", i.e. muted text, so it resolves to the design's --text-secondary.
		textSecondary: {
			main: textSecondaryMain,
			light: primaryLight,
		},
		textLinkHelpSupport: {
			main: darken(secondaryColor, 10),
		},
	};
}
